import React from 'react';
import { NavLink } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Renders one feature-gated sidebar item with three possible states:
 *   - allowed → normal item, navigates to `route`
 *   - locked  → padlock icon next to label, navigates to /dashboard/locked/<key>, dimmed
 *   - hidden  → renders nothing
 *
 * Styling lives in the parent so existing visual conventions (top-level vs.
 * child-link classes) carry over unchanged. Pass the same `className` function
 * the original NavLink used.
 *
 * Props:
 *   featureKey   — feature key (e.g. 'ait')
 *   route        — destination when allowed (e.g. '/dashboard/ait')
 *   className    — function ({ isActive }) => string OR string. Same shape NavLink expects.
 *   children     — icon + label nodes (rendered inside the NavLink)
 *   onClick      — optional click handler (used to close mobile sidebar)
 *   lockedDim    — class string applied additionally when locked (default: 'opacity-60')
 */
export default function SidebarFeatureItem({
    featureKey,
    route,
    className,
    children,
    onClick,
    lockedDim = 'opacity-60'
}) {
    const { canAccess, isLocked } = useAuth();

    const allowed = canAccess(featureKey);
    const locked = !allowed && isLocked(featureKey);

    if (!allowed && !locked) return null;

    const target = allowed ? route : `/dashboard/locked/${featureKey}`;

    const buildClass = (state) => {
        const base = typeof className === 'function' ? className(state) : (className || '');
        return locked ? `${base} ${lockedDim}` : base;
    };

    return (
        <NavLink to={target} className={buildClass} onClick={onClick}>
            {children}
            {locked && <Lock size={14} className="ml-auto text-gray-400" />}
        </NavLink>
    );
}
